import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import { Stack } from '@mui/material';

import { useCheckoutSettings, useUpdateCheckoutSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  ChipListField,
  FeedbackSnackbar,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';

/** Checkout flow configuration. */
export function CheckoutSettingsPage() {
  const { canView, canManage } = useDomainPermissions('checkout');
  const query = useCheckoutSettings();
  const update = useUpdateCheckoutSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Checkout settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Checkout settings"
      subtitle="Guest checkout, required fields, steps and method selection rules."
      icon={<ShoppingCartCheckoutRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Checkout flow" icon={<ShoppingCartCheckoutRoundedIcon />}>
              <SwitchField
                label="Guest checkout"
                description="Allow customers to order without an account."
                checked={draft.guestCheckoutEnabled}
                onChange={(value) => form.setField('guestCheckoutEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Promo codes"
                description="Allow promo codes to be applied at checkout."
                checked={draft.promoCodesEnabled}
                onChange={(value) => form.setField('promoCodesEnabled', value)}
                disabled={disabled}
              />
              <ChipListField
                label="Checkout steps"
                description="Ordered steps shown to the customer."
                value={draft.steps}
                onChange={(value) => form.setField('steps', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title="Required fields"
              description="Fields the customer must complete to place an order."
              icon={<ChecklistRoundedIcon />}
            >
              <ChipListField
                label="Customer fields"
                value={draft.requiredCustomerFields}
                onChange={(value) => form.setField('requiredCustomerFields', value)}
                disabled={disabled}
              />
              <ChipListField
                label="Delivery fields"
                value={draft.requiredDeliveryFields}
                onChange={(value) => form.setField('requiredDeliveryFields', value)}
                disabled={disabled}
              />
              <ChipListField
                label="Payment fields"
                value={draft.requiredPaymentFields}
                onChange={(value) => form.setField('requiredPaymentFields', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Selection rules" icon={<RuleRoundedIcon />}>
              <TextInputField
                label="Delivery method rule"
                description="How delivery methods are offered/selected."
                value={draft.deliveryMethodSelectionRule}
                onChange={(value) => form.setField('deliveryMethodSelectionRule', value)}
                disabled={disabled}
              />
              <TextInputField
                label="Payment method rule"
                description="How payment methods are offered/selected."
                value={draft.paymentMethodSelectionRule}
                onChange={(value) => form.setField('paymentMethodSelectionRule', value)}
                disabled={disabled}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
